import { get } from 'lodash';
import { createSelector } from 'reselect';
import {
  isSubmitting,
  isDirty,
  isValid,
  hasSubmitSucceeded,
  getFormValues,
  getFormInitialValues,
  hasSubmitFailed,
  formValueSelector,
} from 'redux-form';

import { makeUnitsOptions } from 'Common/FormHelper';
import {
  EDIT_RECIPE_FORM_NAME,
  RECIPE_PREPARATION_FORM_NAME,
} from 'Common/FormEnum';
import {
  UserModifiedEnum as UserModifiedFields,
  RecipeFieldEnum as RecipeFields,
  PreparationInstructionEnum as PreparationInstructionFields,
  IngredientFieldEnum as IngredientFields,
  FieldEnum,
} from 'Common/FieldEnum';
import { createMap } from 'Common/Helpers/format';
import { STEP_OPTIONS } from 'Pages/managerecipe/helpers/constants';

const root = (state) => get(state, 'ManageRecipeState.editRecipe');

export const getFetching = (state) => get(root(state), 'fetching');

export const getFetchError = (state) => get(root(state), 'fetchError', null);

export const getFetchInfo = (state) => get(root(state), 'fetchInfo', null);

export const getNutritionFactsFetching = (state) =>
  get(root(state), 'nutritionFactsFetching');

export const getRecipeFormValues = getFormValues(EDIT_RECIPE_FORM_NAME);

export const getRecipeInitialFormValues = getFormInitialValues(
  EDIT_RECIPE_FORM_NAME
);

export const getRecipePrepFormValues = getFormValues(
  RECIPE_PREPARATION_FORM_NAME
);

export const getRecipeFormValue = formValueSelector(EDIT_RECIPE_FORM_NAME);

export const getRecipe = (state) => get(root(state), 'recipe');

export const getIsReadOnly = (state) =>
  get(root(state), `recipe.${FieldEnum.IsReadOnly}`, false);

export const getUserModifiedAllergens = (state) =>
  get(
    getRecipeFormValues(state),
    `${UserModifiedFields.UserModifiedSections}.${UserModifiedFields.Allergens}`
  );

export const getUserModifiedAttributes = (state) =>
  get(
    getRecipeFormValues(state),
    `${UserModifiedFields.UserModifiedSections}.${UserModifiedFields.Attributes}`
  );

export const getUserModifiedMealComponents = (state) =>
  get(
    getRecipeFormValues(state),
    `${UserModifiedFields.UserModifiedSections}.${UserModifiedFields.MealComponents}`
  );

export const isDirtySelector = isDirty(EDIT_RECIPE_FORM_NAME);
export const isValidSelector = isValid(EDIT_RECIPE_FORM_NAME);
export const isSubmittingSelector = isSubmitting(EDIT_RECIPE_FORM_NAME);
export const hasSubmitSucceededSelector = hasSubmitSucceeded(
  EDIT_RECIPE_FORM_NAME
);

export const getHasSubmitFailed = hasSubmitFailed(EDIT_RECIPE_FORM_NAME);

export const getIngredients = (state) =>
  get(
    getFormValues(EDIT_RECIPE_FORM_NAME)(state),
    RecipeFields.Ingredients,
    []
  );
export const getIngredientsMap = createSelector(getIngredients, (ingredients) =>
  createMap(ingredients, 'Id')
);

export const getPreparationInstructions = (state) =>
  get(
    getFormValues(EDIT_RECIPE_FORM_NAME)(state),
    RecipeFields.PreparationInstructions,
    []
  );

export const getAvailableSteps = createSelector(
  getPreparationInstructions,
  (instructions) => {
    const usedSteps = instructions.map(
      (i) => i[PreparationInstructionFields.Step]
    );
    return makeUnitsOptions(STEP_OPTIONS, false).map((step) => {
      if (usedSteps.includes(step.value)) {
        step.disabled = true;
      }
      return step;
    });
  }
);

export const getConvertSuccessful = (state) =>
  get(root(state), 'convertSuccessful');

export const hasSubRecipes = createSelector(getIngredients, (ingredients) => {
  return ingredients.some(
    (i) =>
      i[IngredientFields.CreatedFromRecipeId] &&
      i[IngredientFields.CreatedFromRecipeId].length
  );
});

export const getSaveSuccessful = (state) =>
  get(root(state), 'saveSuccessful', false);

export const getDeleteSuccessful = (state) =>
  get(root(state), 'deleteSuccessful', false);

export const getDeleteError = (state) => get(root(state), 'deleteError');

export const getDeleteFetching = (state) =>
  get(root(state), 'deleteFetching', false);

export const getDuplicateSuccessful = (state) =>
  get(root(state), 'duplicateSuccessful', null);

export const getDuplicateError = (state) => get(root(state), 'duplicateError');

export const getInactiveSuccessful = (state) =>
  get(root(state), 'inactiveSuccessful', false);

export const getInactiveError = (state) => get(root(state), 'inactiveError');

export const getInactiveFetching = (state) =>
  get(root(state), 'inactiveFetching', false);

export const getShowIngredientModal = (state) =>
  get(root(state), 'showIngredientModal', false);

export const getDisplayIngredientModal = createSelector(
  [isDirtySelector, isValidSelector, getSaveSuccessful, getShowIngredientModal],
  (isDirty, isValid, saveSuccessful, showIngredientModal) => {
    return showIngredientModal && isValid && (!isDirty || saveSuccessful);
  }
);

export const getNewRecipeId = (state) => {
  get(root(state), 'newRecipeId', null);
};



// WEBPACK FOOTER //
// ./src/pages/managerecipe/state/editrecipe/selectors.js